import { StatusDot } from '@/components/ui';
import { formatClock } from '@/lib/format';
import type { Conversation } from './api';

export interface ConversationSignal {
  label: string;
  /** accent: work waiting on our side; faint: the other side owes the next email. */
  tone: 'accent' | 'faint';
  /** Lower is more urgent. */
  rank: number;
}

/** Who owes the next step in one thread, in the words used everywhere: list, board, location page. */
export function conversationSignal(c: Conversation): ConversationSignal | null {
  if (c.state === 'closed' || c.state === 'archived') return null;
  const d = c.pending_draft;
  if (d?.state === 'pending_review') return { label: 'Draft ready', tone: 'accent', rank: 0 };
  if (d?.state === 'scheduled') return { label: d.send_not_before ? `Sending ${formatClock(d.send_not_before)}` : 'Sending soon', tone: 'accent', rank: 1 };
  if (c.awaiting === 'us') return { label: 'Reply needed', tone: 'accent', rank: 2 };
  if (c.awaiting === 'them') return { label: 'Awaiting reply', tone: 'faint', rank: 3 };
  return null;
}

export function SignalDot({ signal, className }: { signal: ConversationSignal; className?: string }) {
  return (
    <StatusDot tone={signal.tone} className={className}>
      {signal.label}
    </StatusDot>
  );
}

/** Most urgent signal per location, keyed by each conversation's primary location. */
export function signalsByLocation(items: readonly Conversation[]): Map<string, ConversationSignal> {
  const out = new Map<string, ConversationSignal>();
  for (const c of items) {
    const id = c.primary_location_id ?? c.locations[0]?.location_id;
    const s = id ? conversationSignal(c) : null;
    if (!id || !s) continue;
    const prev = out.get(id);
    if (!prev || s.rank < prev.rank) out.set(id, s);
  }
  return out;
}
